import { useMemo } from 'react';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import { urunKarSiralari, type UrunKar, type UrunKarSatiri } from './analizHesaplari';
import { oranMetni } from './bicim';
import { BlokIskeleti } from './BlokIskeleti';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { useSaSorgu } from './useSatisVerisi';

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  sube?: string | null;
  kanal?: string | null;
  onUrunSec?: (u: { stokKodu: string; urunAdi: string }) => void;
}

function Liste({ baslik, alt, testid, urunler, onUrunSec }: {
  baslik: string; alt: string; testid: string; urunler: UrunKar[]; onUrunSec?: Props['onUrunSec'];
}) {
  return (
    <div data-testid={testid} className="min-w-0 flex flex-col">
      <SectionCard title={baslik} subtitle={alt} className="flex-1">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-ink-400 text-left">
              <th className="font-normal py-1">Ürün</th>
              <th className="font-normal py-1 text-right">Ciro</th>
              <th className="font-normal py-1 pl-2 text-right">Kâr</th>
              <th className="font-normal py-1 pl-2 text-right">Marj</th>
            </tr>
          </thead>
          <tbody>
            {urunler.map((u) => (
              <tr key={u.stokKodu} className="border-t border-border">
                <td className="py-1.5 pr-2 text-ink-800 truncate max-w-0 w-full">
                  {onUrunSec ? (
                    <button
                      type="button"
                      data-testid={`${testid}-urun-${u.stokKodu}`}
                      onClick={() => onUrunSec({ stokKodu: u.stokKodu, urunAdi: u.urunAdi })}
                      className="focus-ring block w-full truncate text-left hover:underline underline-offset-2"
                    >
                      {u.urunAdi}
                    </button>
                  ) : (
                    u.urunAdi
                  )}
                </td>
                <td className="py-1.5 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(u.ciro)}</td>
                <td className="py-1.5 pl-2 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(u.kar)}</td>
                <td className="py-1.5 pl-2 text-right tnum text-ink-500 whitespace-nowrap">{oranMetni(u.marj, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

/** Karlilik : en cok kar birakan ve en dusuk marjli urunler (990056, Kar koprusuyle ayni anahtar; ek istek yok). */
export function UrunKarBlok({ kaynak, donem, sube = null, kanal = null, onUrunSec }: Props) {
  const q = useSaSorgu<UrunKarSatiri>(kaynak, { donem, sube, kanal });
  const { enKarli, dusukMarj } = useMemo(() => urunKarSiralari(q.data?.data ?? []), [q.data]);

  if (!kaynak) return null;
  if (q.isLoading) return <BlokIskeleti testid="blok-urun-kar-iskelet" baslik="En çok kâr bırakanlar" yukseklik={300} satir={10} />;
  if (enKarli.length === 0) return null;

  return (
    <div data-testid="blok-urun-kar" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Liste baslik="En çok kâr bırakanlar" alt="Kâra göre ilk 10" testid="urun-kar-en" urunler={enKarli} onUrunSec={onUrunSec} />
      {dusukMarj.length > 0 && (
        <Liste baslik="Marjı en düşük ürünler" alt="Cirosu ortalamanın üstündekilerden" testid="urun-kar-dusuk" urunler={dusukMarj} onUrunSec={onUrunSec} />
      )}
    </div>
  );
}
